import { Elements, ExpressCheckoutElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeExpressCheckoutElementClickEvent } from "@stripe/stripe-js";
import { ReactNode } from "react";

import { SubscriptionIntent } from "../../../../services/api";
import { getStripe } from "../../../../services/stripe";
import { WalletProbe as ProbeBox } from "./styles";

// Apple Pay and Google Pay through Stripe's Express Checkout Element: their own official buttons (the brands
// require them), each wallet only on devices that have it. Once confirmed in the wallet's sheet, the payment
// takes the card's way: subscription created on the API, payment confirmed by Stripe, PLUS on by the webhook.
// A one-time purchase (a domain) uses the "payment" mode: no recurring charge in the wallet's sheet

export type Wallet = "applePay" | "googlePay";
export type WalletAvailability = Record<Wallet, boolean>;

/** Where Stripe sends the person back after a bank's 3D Secure page: this checkout, for the Keepo being paid. */
export const checkoutReturnUrl = (siteId: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set("site", siteId);
  return url.toString();
};

const NO_OTHER_METHODS = { link: "never", paypal: "never", amazonPay: "never", klarna: "never" } as const;

type ProviderProps = {
  /** What's charged, in cents: the wallet's sheet shows it, and it must match the subscription's first invoice.
   *  Without it, the wallet only saves its card (for the next charges). */
  amount?: number;
  /** A one-time payment (a domain) instead of a subscription's first invoice. */
  oneTime?: boolean;
  children: ReactNode;
};

/** Stripe's elements set up for a payment of this amount (subscription or once), or (no amount) for saving a card. */
const WalletProvider = ({ amount, oneTime, children }: ProviderProps) => (
  <Elements
    stripe={getStripe()}
    options={
      amount === undefined
        ? { mode: "setup", currency: "brl", appearance: { variables: { borderRadius: "12px" } } }
        : {
            mode: oneTime ? "payment" : "subscription",
            amount,
            currency: "brl",
            appearance: { variables: { borderRadius: "12px" } },
          }
    }>
    {children}
  </Elements>
);

/**
 * Finds out which wallets this device has, out of sight: the list only offers those. Stripe tells once its
 * element is ready (none when the domain isn't registered with Stripe, or the browser has no wallet).
 */
export const WalletProbe = ({
  amount,
  oneTime,
  onAvailable,
}: {
  amount?: number;
  oneTime?: boolean;
  onAvailable: (wallets: WalletAvailability) => void;
}) => (
  <ProbeBox aria-hidden="true">
    <WalletProvider amount={amount} oneTime={oneTime}>
      <ExpressCheckoutElement
        options={{ paymentMethods: { ...NO_OTHER_METHODS, applePay: "auto", googlePay: "auto" } }}
        onReady={({ availablePaymentMethods }) =>
          onAvailable({
            applePay: !!availablePaymentMethods?.applePay,
            googlePay: !!availablePaymentMethods?.googlePay,
          })
        }
        onConfirm={() => undefined}
      />
    </WalletProvider>
  </ProbeBox>
);

type ButtonProps = {
  wallet: Wallet;
  /** The first charge, in cents (what the wallet's sheet shows today). */
  amount: number;
  /** What each later charge is, in cents: the plan's price (less a coupon that lasts forever). When it
   *  differs from `amount`, the first period is shown apart (Apple Pay's trial billing). */
  recurringAmount: number;
  /** Shown in the wallet: the plan, and how often it's charged (Apple Pay lists it as a recurring payment). */
  label: string;
  interval: "month" | "year";
  /** Checked before the sheet opens; false keeps it closed (the form shows what's missing). */
  validate: () => boolean;
  createSubscription: () => Promise<SubscriptionIntent>;
  onStart: () => void;
  /** Confirmed in the wallet's sheet: the payment is under way. */
  onConfirm: () => void;
  onCancel: () => void;
  onPaid: (siteId: string) => void;
  onError: (message?: string) => void;
};

const WalletPayButton = ({
  wallet,
  amount,
  recurringAmount,
  label,
  interval,
  validate,
  createSubscription,
  onStart,
  onConfirm,
  onCancel,
  onPaid,
  onError,
}: ButtonProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const click = (event: StripeExpressCheckoutElementClickEvent) => {
    if (!validate()) {
      // Not resolving it keeps the sheet closed
      return;
    }
    onStart();
    const hasIntroPeriod = amount !== recurringAmount;
    const secondPeriodStart = new Date();
    if (interval === "year") {
      secondPeriodStart.setFullYear(secondPeriodStart.getFullYear() + 1);
    } else {
      secondPeriodStart.setMonth(secondPeriodStart.getMonth() + 1);
    }
    event.resolve({
      applePay: {
        recurringPaymentRequest: {
          paymentDescription: label,
          managementURL: `${window.location.origin}/subscription/list`,
          // A coupon that doesn't last forever: the first period at its price, the plan's price after it
          ...(hasIntroPeriod && {
            trialBilling: {
              label,
              amount,
              recurringPaymentIntervalUnit: interval,
              recurringPaymentIntervalCount: 1,
            },
          }),
          regularBilling: {
            label,
            amount: recurringAmount,
            recurringPaymentIntervalUnit: interval,
            recurringPaymentIntervalCount: 1,
            ...(hasIntroPeriod && { recurringPaymentStartDate: secondPeriodStart }),
          },
        },
      },
    });
  };

  const confirm = async () => {
    onConfirm();
    if (!stripe || !elements) {
      onError();
      return;
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      onError(submitError.message);
      return;
    }
    try {
      const intent = await createSubscription();
      // Only when the bank's 3D Secure needs a redirect; it comes back to the checkout with the Keepo that was paying
      const confirmParams = { return_url: checkoutReturnUrl(intent.siteId) };
      // Nothing to pay (a 100% coupon): the wallet's card is still saved for the next charges
      if (!intent.clientSecret) {
        if (intent.setupClientSecret) {
          const { error: setupError } = await stripe.confirmSetup({
            elements,
            clientSecret: intent.setupClientSecret,
            confirmParams,
            redirect: "if_required",
          });
          if (setupError) {
            onError(setupError.message);
            return;
          }
        }
        onPaid(intent.siteId);
        return;
      }
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret: intent.clientSecret,
        confirmParams,
        redirect: "if_required",
      });
      if (error) {
        onError(error.message);
        return;
      }
      onPaid(intent.siteId);
    } catch {
      onError();
    }
  };

  return (
    <ExpressCheckoutElement
      options={{
        paymentMethods: {
          ...NO_OTHER_METHODS,
          applePay: wallet === "applePay" ? "auto" : "never",
          googlePay: wallet === "googlePay" ? "auto" : "never",
        },
        buttonType: { applePay: "subscribe", googlePay: "subscribe" },
        buttonTheme: { applePay: "black", googlePay: "black" },
        buttonHeight: 48,
      }}
      onClick={click}
      onConfirm={confirm}
      onCancel={onCancel}
    />
  );
};

/** The wallet's official button, ready to subscribe with it. */
export const WalletButton = (props: ButtonProps) => (
  <WalletProvider amount={props.amount}>
    <WalletPayButton {...props} />
  </WalletProvider>
);

type SetupButtonProps = {
  wallet: Wallet;
  /** A SetupIntent for the subscription's customer (see api.createCardSetup). */
  createSetup: () => Promise<{ clientSecret: string }>;
  onStart: () => void;
  onCancel: () => void;
  /** The wallet's card, saved: it becomes the subscription's (api.updatePaymentMethod). */
  onSaved: (paymentMethodId: string) => void;
  onError: (message?: string) => void;
};

const WalletSetupInner = ({ wallet, createSetup, onStart, onCancel, onSaved, onError }: SetupButtonProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const confirm = async () => {
    if (!stripe || !elements) {
      onError();
      return;
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      onError(submitError.message);
      return;
    }
    try {
      const { clientSecret } = await createSetup();
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (error || !setupIntent?.payment_method) {
        onError(error?.message);
        return;
      }
      onSaved(
        typeof setupIntent.payment_method === "string" ? setupIntent.payment_method : setupIntent.payment_method.id
      );
    } catch {
      onError();
    }
  };

  return (
    <ExpressCheckoutElement
      options={{
        paymentMethods: {
          ...NO_OTHER_METHODS,
          applePay: wallet === "applePay" ? "auto" : "never",
          googlePay: wallet === "googlePay" ? "auto" : "never",
        },
        buttonType: { applePay: "plain", googlePay: "plain" },
        buttonTheme: { applePay: "black", googlePay: "black" },
        buttonHeight: 48,
      }}
      onClick={event => {
        onStart();
        event.resolve();
      }}
      onConfirm={confirm}
      onCancel={onCancel}
    />
  );
};

/** The wallet's official button, to save its card for the subscription's next charges (managing it). */
export const WalletSetupButton = (props: SetupButtonProps) => (
  <WalletProvider>
    <WalletSetupInner {...props} />
  </WalletProvider>
);

type PaymentButtonProps = {
  wallet: Wallet;
  /** In cents. */
  amount: number;
  /** Checked before the sheet opens; false keeps it closed (the form shows what's missing). */
  validate: () => boolean;
  /** The payment to confirm (e.g. a domain's order): its client secret. */
  createPayment: () => Promise<{ clientSecret?: string }>;
  onStart: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onPaid: () => void;
  onError: (message?: string) => void;
};

const WalletPaymentInner = ({
  wallet,
  validate,
  createPayment,
  onStart,
  onConfirm,
  onCancel,
  onPaid,
  onError,
}: PaymentButtonProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const confirm = async () => {
    onConfirm();
    if (!stripe || !elements) {
      onError();
      return;
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      onError(submitError.message);
      return;
    }
    try {
      const { clientSecret } = await createPayment();
      if (!clientSecret) {
        onError();
        return;
      }
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        // Only when the bank's 3D Secure needs a redirect; it comes back here
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (error) {
        onError(error.message);
        return;
      }
      onPaid();
    } catch (error) {
      onError(error instanceof Error ? error.message : undefined);
    }
  };

  return (
    <ExpressCheckoutElement
      options={{
        paymentMethods: {
          ...NO_OTHER_METHODS,
          applePay: wallet === "applePay" ? "auto" : "never",
          googlePay: wallet === "googlePay" ? "auto" : "never",
        },
        buttonType: { applePay: "buy", googlePay: "buy" },
        buttonTheme: { applePay: "black", googlePay: "black" },
        buttonHeight: 48,
      }}
      onClick={event => {
        if (!validate()) {
          // Not resolving it keeps the sheet closed
          return;
        }
        onStart();
        event.resolve();
      }}
      onConfirm={confirm}
      onCancel={onCancel}
    />
  );
};

/** The wallet's official button, to pay something once (a domain). */
export const WalletPaymentButton = (props: PaymentButtonProps) => (
  <WalletProvider amount={props.amount} oneTime>
    <WalletPaymentInner {...props} />
  </WalletProvider>
);
